import type { IOGDisplayConfigFormValues } from '../../pages/companies-platform-configs/components/OGDisplayConfigForm';
import type { IFinnConfigFormValues } from '../../pages/companies-platform-configs/components/FinnConfigForm';
import type { ISnapChatConfigFormValues } from '../../pages/companies-platform-configs/components/SnapChatConfigForm';
import type { IDeltaConfigFormValues } from '../../pages/companies-platform-configs/components/DeltaMediaConfigForm';
import type { ICompanyPlatformConfigurationsModel } from '@/shared/models/companies/platform-configs/company-platform-configurations.model';
import { api } from '@/shared/services/api/api';
import type { IMetaConfigFormValues } from '../../pages/companies-platform-configs/components/MetaConfigForm';
import type { IBidtheatreConfigFormValues } from '@/admin/shared/types/bidtheatre';
import type { IBoardsConfigFormValues } from '@/admin/shared/types/boards';
import type { AvailablePlatformsEnum } from '@/editor/shared/constants/available-platforms';
import type { IGoogleConfigFormValues } from '@/admin/pages/companies-platform-configs/components/GoogleConfigForm';
import type { GeneralPlatformConfigRequest } from '@/admin/pages/companies-platform-configs/components/GeneralConfigForm';

const baseURL = 'admin/company-platform-configs';

type PlatformConfigFormMap = {
  [AvailablePlatformsEnum.META]: IMetaConfigFormValues;
  [AvailablePlatformsEnum.DELTA]: IDeltaConfigFormValues;
  [AvailablePlatformsEnum.SNAPCHAT]: ISnapChatConfigFormValues;
  [AvailablePlatformsEnum.FINN]: IFinnConfigFormValues;
  [AvailablePlatformsEnum.OGDISPLAY]: IOGDisplayConfigFormValues;
  [AvailablePlatformsEnum.BIDTHEATRE]: IBidtheatreConfigFormValues;
  [AvailablePlatformsEnum.BOARDS]: IBoardsConfigFormValues;
  [AvailablePlatformsEnum.GOOGLE_ADS]: IGoogleConfigFormValues;
};

type PlatformConfigRequest<T extends AvailablePlatformsEnum> = {
  values: PlatformConfigFormMap[T];
  id?: string;
  companyId: string;
  platform: T;
  bidtheatreAdType?: string | null | undefined;
};

type AnyPlatformConfigRequest =
  | PlatformConfigRequest<AvailablePlatformsEnum.META>
  | PlatformConfigRequest<AvailablePlatformsEnum.DELTA>
  | PlatformConfigRequest<AvailablePlatformsEnum.SNAPCHAT>
  | PlatformConfigRequest<AvailablePlatformsEnum.FINN>
  | PlatformConfigRequest<AvailablePlatformsEnum.OGDISPLAY>
  | PlatformConfigRequest<AvailablePlatformsEnum.BIDTHEATRE>
  | PlatformConfigRequest<AvailablePlatformsEnum.BOARDS>
  | PlatformConfigRequest<AvailablePlatformsEnum.GOOGLE_ADS>;

export const companiesPlatformConfigsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPlatformConfigsByCompanyId: build.query<ICompanyPlatformConfigurationsModel, { id: string }>({
      query: ({ id }) => ({
        url: `admin/companies/${id}/platform-configurations`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'CompanyPlatformsConfigs', id: arg.id }],
    }),
    createPlatformConfig: build.mutation<void, AnyPlatformConfigRequest>({
      query: ({ values, companyId, platform, bidtheatreAdType }) => ({
        url: bidtheatreAdType ? `${baseURL}/${platform}/${bidtheatreAdType}` : `${baseURL}/${platform}`,
        method: 'POST',
        body: { ...values, companyId },
      }),
      invalidatesTags: ['CompanyPlatformsConfigs'],
    }),
    updatePlatformConfig: build.mutation<void, AnyPlatformConfigRequest>({
      query: ({ values, id, platform }) => ({
        url: `${baseURL}/${platform}/${id}`,
        method: 'PUT',
        body: values,
      }),
    }),
    createUpdateGeneralPlatformConfig: build.mutation<void, GeneralPlatformConfigRequest>({
      query: ({ values, companyId }) => ({
        url: `${baseURL}/general`,
        method: 'POST',
        body: { ...values, companyId },
      }),
      invalidatesTags: ['CompanyPlatformsConfigs'],
    }),
    applyToOffices: build.mutation<any, any>({
      query: ({ companyId, platform, bidtheatreAdType }) => ({
        url: `${baseURL}/apply-to-offices`,
        method: 'POST',
        body: { companyId, platform, bidtheatreAdType },
      }),
    }),
  }),
});

export const {
  useGetPlatformConfigsByCompanyIdQuery,
  useCreatePlatformConfigMutation,
  useUpdatePlatformConfigMutation,
  useCreateUpdateGeneralPlatformConfigMutation,
  useApplyToOfficesMutation,
} = companiesPlatformConfigsApi;

//   useCreateMetaConfigMutation,

//   useCreateDeltaConfigMutation,
//   useCreateSnapchatConfigMutation,
//   useUpdateSnapchatConfigMutation,
//   useUpdateDeltaConfigMutation,
//   useUpdateMetaConfigMutation,
//   useCreateFinnConfigMutation,
//   useUpdateFinnConfigMutation,
//   useCreateOgDisplayConfigMutation,
//   useUpdateOgDisplayConfigMutation,
//   useCompanyBidTheatreConfigMutation,
//   useCompanyBoardsConfigMutation,
